import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, FileDown, Clock, FileText, Printer } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, apiError, fmtDuration, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";

const mondayOf = (d) => {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
};
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function Reports() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [week, setWeek] = useState(() => mondayOf(new Date()));
  const [data, setData] = useState(null);

  const openJobPdf = (jobId) => {
    const token = getToken();
    const a = document.createElement("a");
    a.href = `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}/pdf?auth=${token}`;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const load = useCallback(async () => {
    try {
      const res = await api.get("/reports/time", { params: { week_start: ymd(week) } });
      setData(res.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [week]);

  useEffect(() => { load(); }, [load]);

  const shift = (dir) => {
    const d = new Date(week);
    d.setDate(d.getDate() + dir * 7);
    setWeek(mondayOf(d));
  };

  const downloadPdf = () => {
    const token = getToken();
    window.open(
      `${process.env.REACT_APP_BACKEND_URL}/api/reports/time/pdf?week_start=${ymd(week)}&auth=${token}`,
      "_blank",
      "noopener"
    );
  };

  return (
    <div data-testid="reports-page">
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">{t("reports.title")}</h1>
          <p className="text-sm text-slate-500" data-testid="reports-week">
            {data ? `${data.week_start} – ${data.week_end}` : t("common.loading")}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="icon" data-testid="reports-prev" onClick={() => shift(-1)} className="h-10 w-10 bg-white">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" data-testid="reports-this-week" onClick={() => setWeek(mondayOf(new Date()))} className="h-10 bg-white">
            {t("reports.thisWeek")}
          </Button>
          <Button variant="outline" size="icon" data-testid="reports-next" onClick={() => shift(1)} className="h-10 w-10 bg-white">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            data-testid="reports-pdf-button"
            onClick={downloadPdf}
            className="h-10 bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700"
          >
            <FileDown className="mr-1.5 h-4 w-4" /> PDF
          </Button>
        </div>
      </div>

      {data && (
        <>
          <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {[
              [t("reports.workTime"), data.totals.work_seconds, "total-work"],
              [t("reports.travel"), data.totals.travel_seconds, "total-travel"],
              [t("reports.pause"), data.totals.pause_seconds, "total-pause"],
            ].map(([label, secs, tid]) => (
              <div key={tid} className="rounded-lg border border-slate-200 bg-white p-4">
                <p className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">{label}</p>
                <p className="font-heading text-2xl font-bold text-slate-900" data-testid={`reports-${tid}`}>
                  {fmtDuration(secs)}
                </p>
              </div>
            ))}
          </div>

          <h2 className="mb-2 text-xl font-semibold text-slate-800">{t("reports.perTech")}</h2>
          <div className="mb-5 overflow-hidden rounded-lg border border-slate-200 bg-white">
            {data.per_technician.length === 0 && (
              <p className="p-6 text-center text-sm text-slate-500" data-testid="reports-empty">
                {t("reports.noTimes")}
              </p>
            )}
            {data.per_technician.map((tech) => (
              <div
                key={tech.user_id}
                data-testid={`reports-tech-${tech.user_id}`}
                className="flex flex-wrap items-center gap-3 border-b border-slate-100 p-3 last:border-0"
              >
                <span className="min-w-0 flex-1 text-sm font-semibold text-slate-900">{tech.user_name}</span>
                <span className="text-xs text-slate-500">{tech.jobs} {t("reports.ordersCount")}</span>
                <span className="flex items-center gap-1.5 text-sm font-medium text-slate-900">
                  <Clock className="h-3.5 w-3.5 text-slate-400" /> {fmtDuration(tech.work_seconds)}
                </span>
                <span className="text-sm text-slate-500">{t("common.travel")} {fmtDuration(tech.travel_seconds)}</span>
                <span className="text-sm text-slate-500">{t("common.pause")} {fmtDuration(tech.pause_seconds)}</span>
              </div>
            ))}
          </div>

          <h2 className="mb-2 text-xl font-semibold text-slate-800">{t("reports.perOrder")}</h2>
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.05em] text-slate-500">
                <tr>
                  <th className="px-3 py-2">{t("common.date")}</th>
                  <th className="px-3 py-2">{t("reports.order")}</th>
                  <th className="px-3 py-2">{t("reports.customer")}</th>
                  <th className="px-3 py-2">{t("reports.technician")}</th>
                  <th className="px-3 py-2">{t("reports.workTime")}</th>
                  <th className="px-3 py-2">{t("reports.travel")}</th>
                  <th className="px-3 py-2">{t("reports.action")}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={`${r.job_id}-${r.user_id}-${i}`} data-testid={`reports-row-${r.job_id}`} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-600">{r.date}</td>
                    <td className="px-3 py-2 font-medium text-slate-900">
                      #{r.order_number ? String(r.order_number).padStart(4, "0") : "–"} {r.title}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{r.customer_name || "–"}</td>
                    <td className="px-3 py-2 text-slate-600">{r.user_name}</td>
                    <td className="px-3 py-2 font-medium">{fmtDuration(r.work_seconds)}</td>
                    <td className="px-3 py-2 text-slate-600">{fmtDuration(r.travel_seconds)}</td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1.5">
                        <Button
                          size="sm"
                          data-testid={`reports-open-${r.job_id}`}
                          onClick={() => navigate(`/arbetsorder/${r.job_id}`)}
                          className="h-8 bg-blue-600 px-2 text-white transition-colors duration-200 hover:bg-blue-700"
                        >
                          {t("common.open")}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`reports-job-pdf-${r.job_id}`}
                          onClick={() => openJobPdf(r.job_id)}
                          className="h-8 bg-white px-2 transition-colors duration-200 hover:bg-slate-100"
                        >
                          <FileText className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`reports-job-print-${r.job_id}`}
                          onClick={() => openJobPdf(r.job_id)}
                          className="h-8 bg-white px-2 transition-colors duration-200 hover:bg-slate-100"
                        >
                          <Printer className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
